import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { fireTag } from '@vue-util/tagManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import useSiteMonGAMTags from '@modules/discovery/composables/useSitemonGAMTags';
import { deviceProductsPerRow } from '@utils/constants';
import useAltGridViewExperimentation from './useAltGridViewExperiment';

export default function useAltGridView(emit) {
  const store = useStore();
  const enableAsyncAnalyticsTags = store?.state?.envProps?.enableAsyncAnalyticsTags;
  const { getSiteMonGAMTagsOnFacetSelection } = useSiteMonGAMTags();
  const {
    altGridViewEnabled, desktopAltEnabled, phoneAltEnabled1, phoneAltEnabled2, noOfDesktopProds, noOfPhoneProds,
    controlAltDesktopEnabled, controlAltPhoneEnabled,
  } = useAltGridViewExperimentation();

  const altGridViewClasses = ref(`small-up-${noOfPhoneProds.value} medium-up-${noOfDesktopProds.value} large-up-${noOfDesktopProds.value}`);

  const currNumberOfProducts = computed(() => {
    if (store.getters.getAltGridViewProdsPerRow) {
      return store.getters.getAltGridViewProdsPerRow;
    }
    return (desktopAltEnabled.value || controlAltDesktopEnabled.value) ? noOfDesktopProds.value : noOfPhoneProds.value;
  });

  const selectedGridView = ref(currNumberOfProducts.value);

  const standardImage = computed(() => {
    const standardProducts = desktopAltEnabled.value || controlAltDesktopEnabled.value ? deviceProductsPerRow.DESKTOP : deviceProductsPerRow.PHONE;
    return selectedGridView.value === standardProducts ? require('@images/alt-grid-views/Standard_Selected.svg')
      : require('@images/alt-grid-views/Standard_Unselected.svg');
  });

  const smallImage = computed(() => {
    const smallProducts = desktopAltEnabled.value || controlAltDesktopEnabled.value
      ? deviceProductsPerRow.DESKTOPALT : deviceProductsPerRow.PHONEALT1;
    return selectedGridView.value === smallProducts ? require('@images/alt-grid-views/Small_Selected.svg')
      : require('@images/alt-grid-views/Small_Unselected.svg');
  });

  const portraitImage = computed(() => (
    selectedGridView.value === deviceProductsPerRow.PHONEALT2 ? require('@images/alt-grid-views/Portrait_Selected.svg')
      : require('@images/alt-grid-views/Portrait_Unselected.svg')
  ));

  const isSmallAltGridViewCard = computed(() => currNumberOfProducts.value === deviceProductsPerRow.DESKTOPALT
    || currNumberOfProducts.value === deviceProductsPerRow.PHONEALT1);

  function altGridViewAnalyticsWeb(treatment, isControl) {
    let experiment;
    if (isControl) {
      experiment = treatment ? '2665-21' : '2555-20';
    } else {
      experiment = treatment ? '2561-21' : '2561-20';
    }
    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'experiment action',
        link_name: 'experiment action',
        experiment_key: `AltGridLayout:${experiment}:minimal grid web toggle click`,
        experiment_key_type: 'action',
      }));
    } else {
      fireTag('link', {
        event_name: 'experiment action',
        link_name: 'experiment action',
        experiment_key: `AltGridLayout:${experiment}:minimal grid web toggle click`,
        experiment_key_type: 'action',
      });
    }
  }

  function altGridViewAnalyticsMew(treatment, isControl, products) {
    let experimentKey;
    if (isControl) {
      const experimentKeyMap = {
        1: 'AltGridLayout:2665-21:portrait toggle click mew',
        2: 'AltGridLayout:2665-21:control 2 column toggle click mew',
        3: 'AltGridLayout:2665-21:treatment 3 column toggle click mew',
      };
      experimentKey = experimentKeyMap[products];
    } else if (treatment) {
      experimentKey = phoneAltEnabled1.value
        ? 'AltGridLayout:2562-21:treatment 3 column toggle click mew'
        : 'AltGridLayout:2562-22:portrait toggle click mew';
    } else {
      experimentKey = 'AltGridLayout:2562-20:control 2 column toggle click mew';
    }
    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'experiment action',
        link_name: 'experiment action',
        experiment_key: experimentKey,
        experiment_key_type: 'action',
      }));
    } else {
      fireTag('link', {
        event_name: 'experiment action',
        link_name: 'experiment action',
        experiment_key: experimentKey,
        experiment_key_type: 'action',
      });
    }
  }

  function animateClass(products) {
    if (!desktopAltEnabled.value && !controlAltDesktopEnabled.value) {
      const phoneAnimations = {
        1: 'alt-grid-animate-portrait-1',
        2: 'alt-grid-animate-portrait-2',
        3: 'alt-grid-animate-portrait-3',
      };
      return phoneAnimations[products];
    }

    const desktopAnimations = {
      4: 'alt-grid-animate-1',
      6: 'alt-grid-animate-2',
    };

    return desktopAnimations[products];
  }

  function changeGridView(products) {
    const animate = animateClass(products);
    selectedGridView.value = products;

    if (desktopAltEnabled.value || controlAltDesktopEnabled.value) {
      altGridViewClasses.value = `small-up-2 medium-up-${products} large-up-${products} ${animate}`;
      const isAltView = products === deviceProductsPerRow.DESKTOPALT;

      altGridViewAnalyticsWeb(
        isAltView,
        controlAltDesktopEnabled.value,
      );
    } else {
      altGridViewClasses.value = `small-up-${products} medium-up-4 large-up-4 ${animate}`;
      const isAltView = products === deviceProductsPerRow.PHONEALT1 || products === deviceProductsPerRow.PHONEALT2;

      altGridViewAnalyticsMew(
        isAltView,
        controlAltPhoneEnabled.value,
        products,
      );
    }

    store.commit('SET_ALT_GRID_VIEW_PRODUCTS', parseInt(products, 10));
    emit('updateGridView', altGridViewClasses.value);
    getSiteMonGAMTagsOnFacetSelection();
  }

  return {
    altGridViewEnabled,
    desktopAltEnabled,
    phoneAltEnabled1,
    phoneAltEnabled2,
    controlAltDesktopEnabled,
    controlAltPhoneEnabled,
    noOfDesktopProds,
    noOfPhoneProds,
    altGridViewClasses,
    changeGridView,
    currNumberOfProducts,
    isSmallAltGridViewCard,
    standardImage,
    smallImage,
    portraitImage,
    altGridViewAnalyticsMew,
    altGridViewAnalyticsWeb,
    animateClass,
    selectedGridView,
  };
}
